import { atr } from "./indicators";

export const DEFAULT_SETTINGS = {
  capital: 10000,
  allocPct: 100, // % of equity put into each position (100 = fully invested, no leverage)
  spreadBps: 1, // full bid/ask spread in basis points of price (1 bp = 0.01%), half paid per side
  commissionBps: 0, // per side
  slippageBps: 0.5, // per side
  entryOrderType: "market", // "market" | "limit" | "stop", valid for the next candle only
  entryOffsetBps: 0,
  longFinancingBpsPerDay: 0, // carrying cost charged for each elapsed 24 hours in a long position
  shortFinancingBpsPerDay: 0, // positive = cost; negative = credit
  slType: "off", // stop loss distance: "off" | "atr" (multiples of ATR14) | "pct" (% of entry price)
  slValue: 2,
  tpType: "off", // take profit: "off" | "atr" | "pct" | "rr" (reward:risk multiple of the stop distance, needs a stop loss)
  tpValue: 4,
  trailing: false, // trail the stop behind the best price since entry (needs a stop loss)
  allowShort: true,
};

/** OHLC entry fill. Limit/stop orders use the prior close as their price anchor and expire
 * after the next candle. A gap through an order fills at the candle open. */
function entryFillPrice(side, signalClose, candle, settings) {
  if (settings.entryOrderType !== "limit" && settings.entryOrderType !== "stop") return candle.open;
  const anchor = Number(signalClose);
  const offset = Math.max(0, Number(settings.entryOffsetBps) || 0) / 1e4;
  if (!Number.isFinite(anchor) || anchor <= 0) return NaN;
  const buy = side === 1;
  const isLimit = settings.entryOrderType === "limit";
  const price = anchor * (buy === isLimit ? 1 - offset : 1 + offset);
  if (isLimit) {
    if (buy && candle.open <= price) return candle.open;
    if (!buy && candle.open >= price) return candle.open;
    if ((buy && candle.low <= price) || (!buy && candle.high >= price)) return price;
    return NaN;
  }
  if (buy && candle.open >= price) return candle.open;
  if (!buy && candle.open <= price) return candle.open;
  if ((buy && candle.high >= price) || (!buy && candle.low <= price)) return price;
  return NaN;
}

/** Backtest the signed number of open trade slots for strategies that support pyramiding.
 *  Each slot receives an equal share of the configured allocation, so using more slots does
 *  not increase total exposure beyond allocPct. */
function runMultiTradeBacktest(candles, desired, settings, maxTrades) {
  const s = { ...DEFAULT_SETTINGS, ...settings, maxTrades };
  const n = candles.length;
  const alloc = Math.max(0, s.allocPct) / 100;
  const unitAlloc = alloc / maxTrades;
  const sideCost = (s.spreadBps / 2 + s.commissionBps + s.slippageBps) / 1e4;
  const useSl = (s.slType === "atr" || s.slType === "pct") && s.slValue > 0;
  const useTp = (s.tpType === "atr" || s.tpType === "pct" || (s.tpType === "rr" && useSl)) && s.tpValue > 0;
  const needAtr = (useSl && s.slType === "atr") || (useTp && s.tpType === "atr");
  const atrArr = needAtr ? atr(candles.map((c) => c.high), candles.map((c) => c.low), candles.map((c) => c.close), 14) : null;
  const distance = (type, value, price, i) => {
    if (type === "pct") return (price * value) / 100;
    const a = atrArr && i > 0 ? atrArr[i - 1] : NaN;
    return Number.isFinite(a) ? a * value : NaN;
  };

  const times = new Array(n);
  const equity = new Array(n);
  const barReturns = new Array(n).fill(0);
  const position = new Array(n).fill(0);
  const trades = [];
  let eq = s.capital;
  let lots = [];
  let blockSide = 0;

  const moveLots = (active, move) => {
    if (!active.length || !Number.isFinite(move)) return;
    const total = active.reduce((sum, lot) => sum + lot.side * unitAlloc * move, 0);
    eq *= 1 + total;
    active.forEach((lot) => {
      lot.balance *= 1 + lot.side * unitAlloc * move;
    });
  };
  const charge = (lot) => {
    const factor = 1 - sideCost * unitAlloc;
    eq *= factor;
    lot.balance *= factor;
  };
  const closeLot = (i, lot, price, reason) => {
    charge(lot);
    const pnl = lot.balance - lot.eqBefore;
    trades.push({
      side: lot.side,
      entryIdx: lot.idx,
      entryTime: candles[lot.idx].time,
      entryPrice: lot.price,
      exitIdx: i,
      exitTime: candles[i].time,
      exitPrice: price,
      ret: lot.eqBefore > 0 ? pnl / lot.eqBefore : 0,
      pnl,
      bars: i - lot.idx,
      reason,
      risk: lot.risk,
      rMultiple: Number.isFinite(lot.risk) && lot.risk > 0 ? (lot.side * (price - lot.price)) / lot.risk : NaN,
    });
  };
  const addLot = (i, side) => {
    const price = entryFillPrice(side, candles[i - 1]?.close, candles[i], s);
    if (!Number.isFinite(price)) return false;
    const eqBefore = eq;
    const balance = eqBefore * (1 - sideCost * unitAlloc);
    eq = balance;
    const risk = useSl ? distance(s.slType, s.slValue, price, i) : NaN;
    const targetDistance = !useTp
      ? NaN
      : s.tpType === "rr"
        ? (Number.isFinite(risk) ? risk * s.tpValue : NaN)
        : distance(s.tpType, s.tpValue, price, i);
    lots.push({
      side,
      idx: i,
      price,
      eqBefore,
      balance,
      risk,
      best: price,
      enteredThisBar: true,
      stopPrice: Number.isFinite(risk) ? price - side * risk : NaN,
      targetPrice: Number.isFinite(targetDistance) ? price + side * targetDistance : NaN,
    });
    return true;
  };

  for (let i = 0; i < n; i += 1) {
    const candle = candles[i];
    times[i] = candle.time;
    const prevEq = i === 0 ? s.capital : equity[i - 1];

    if (lots.length && i > 0) {
      const elapsedDays = Math.max(0, (candle.time - candles[i - 1].time) / 86400);
      const dailyBps = lots[0].side === 1 ? Number(s.longFinancingBpsPerDay) : Number(s.shortFinancingBpsPerDay);
      const rate = Number.isFinite(dailyBps) ? (dailyBps / 1e4) * elapsedDays * unitAlloc : 0;
      if (rate) {
        eq *= 1 - rate * lots.length;
        lots.forEach((lot) => { lot.balance *= 1 - rate; });
      }
    }

    if (lots.length && i > 0) {
      moveLots(lots, candle.open / candles[i - 1].close - 1);
    }

    // Gaps can close a trade before the next-close signal is filled.
    const survivorsAfterGap = [];
    for (const lot of lots) {
      const gapStop = Number.isFinite(lot.stopPrice) && ((lot.side === 1 && candle.open <= lot.stopPrice) || (lot.side === -1 && candle.open >= lot.stopPrice));
      const gapTarget = Number.isFinite(lot.targetPrice) && ((lot.side === 1 && candle.open >= lot.targetPrice) || (lot.side === -1 && candle.open <= lot.targetPrice));
      if (gapStop || gapTarget) {
        closeLot(i, lot, candle.open, gapStop ? "stop" : "target");
        blockSide = lot.side;
      } else survivorsAfterGap.push(lot);
    }
    lots = survivorsAfterGap;

    let target = i > 0 ? Math.round(Number(desired[i - 1]) || 0) : 0;
    target = Math.min(maxTrades, Math.max(-maxTrades, target));
    if (!s.allowShort && target < 0) target = 0;
    if (blockSide !== 0) {
      if (Math.sign(target) === blockSide) target = 0;
      else blockSide = 0;
    }

    const targetSide = Math.sign(target);
    const openSide = lots.length ? lots[0].side : 0;
    if (openSide !== targetSide) {
      lots.slice().forEach((lot) => closeLot(i, lot, candle.open, "signal"));
      lots = [];
    }
    const desiredCount = targetSide === 0 ? 0 : Math.abs(target);
    while (lots.length > desiredCount) {
      const lot = lots.pop();
      closeLot(i, lot, candle.open, "signal");
    }
    while (lots.length < desiredCount) {
      if (!addLot(i, targetSide)) break;
    }

    if (lots.length) {
      const barMoves = lots.map((lot) => {
        const hitStop = Number.isFinite(lot.stopPrice) && ((lot.side === 1 && candle.low <= lot.stopPrice) || (lot.side === -1 && candle.high >= lot.stopPrice));
        const hitTarget = Number.isFinite(lot.targetPrice) && ((lot.side === 1 && candle.high >= lot.targetPrice) || (lot.side === -1 && candle.low <= lot.targetPrice));
        const reason = hitStop ? "stop" : hitTarget ? "target" : "";
        return { lot, price: hitStop ? lot.stopPrice : hitTarget ? lot.targetPrice : candle.close, reason };
      });
      const totalMove = barMoves.reduce((sum, event) => sum + event.lot.side * unitAlloc * (event.price / (event.lot.enteredThisBar ? event.lot.price : candle.open) - 1), 0);
      eq *= 1 + totalMove;
      barMoves.forEach(({ lot, price }) => {
        lot.balance *= 1 + lot.side * unitAlloc * (price / (lot.enteredThisBar ? lot.price : candle.open) - 1);
      });

      const survivorsAfterBar = [];
      for (const { lot, price, reason } of barMoves) {
        if (reason) {
          closeLot(i, lot, price, reason);
          blockSide = lot.side;
        } else {
          if (s.trailing && Number.isFinite(lot.risk) && !lot.enteredThisBar) {
            lot.best = lot.side === 1 ? Math.max(lot.best, candle.high) : Math.min(lot.best, candle.low);
            const trail = lot.best - lot.side * lot.risk;
            lot.stopPrice = lot.side === 1 ? Math.max(lot.stopPrice, trail) : Math.min(lot.stopPrice, trail);
          }
          lot.enteredThisBar = false;
          survivorsAfterBar.push(lot);
        }
      }
      lots = survivorsAfterBar;
    }

    position[i] = lots.length ? lots[0].side : 0;
    equity[i] = eq;
    barReturns[i] = prevEq > 0 ? eq / prevEq - 1 : 0;
  }

  if (lots.length && n > 0) {
    lots.slice().forEach((lot) => closeLot(n - 1, lot, candles[n - 1].close, "end"));
    lots = [];
    equity[n - 1] = eq;
    barReturns[n - 1] = (n > 1 ? eq / equity[n - 2] : eq / s.capital) - 1;
    position[n - 1] = 0;
  }

  return { times, equity, barReturns, position, trades, settings: s };
}

/** Bar-by-bar backtest.
 *  candles: [{time, open, high, low, close}] oldest first.
 *  desired: desired position per bar close (1/-1/0), entered using the selected next-candle order.
 *  Equity is marked to market every bar, trading costs are charged on every fill.
 *  Stop loss / take profit are checked inside each bar. If one bar touches BOTH, the stop is
 *  assumed to be hit first (the cautious choice, since the real order of ticks is unknown). */
export function runBacktest(candles, desired, settings = {}) {
  const maxTrades = Math.min(100, Math.max(1, Math.floor(Number(settings.maxTrades) || 1)));
  if (maxTrades > 1) return runMultiTradeBacktest(candles, desired, settings, maxTrades);
  const s = { ...DEFAULT_SETTINGS, ...settings };
  const n = candles.length;
  const alloc = Math.max(0, s.allocPct) / 100;
  const sideCost = (s.spreadBps / 2 + s.commissionBps + s.slippageBps) / 1e4; // per unit of notional, per fill
  const useSl = (s.slType === "atr" || s.slType === "pct") && s.slValue > 0;
  const useTp = (s.tpType === "atr" || s.tpType === "pct" || (s.tpType === "rr" && useSl)) && s.tpValue > 0;
  const needAtr = (useSl && s.slType === "atr") || (useTp && s.tpType === "atr");
  const atrArr = needAtr ? atr(candles.map((c) => c.high), candles.map((c) => c.low), candles.map((c) => c.close), 14) : null;
  const distance = (type, value, price, i) => {
    if (type === "pct") return (price * value) / 100;
    const a = atrArr && i > 0 ? atrArr[i - 1] : NaN;
    return Number.isFinite(a) ? a * value : NaN;
  };

  const times = new Array(n);
  const equity = new Array(n);
  const barReturns = new Array(n).fill(0);
  const position = new Array(n).fill(0);
  const trades = [];

  let eq = s.capital;
  let pos = 0;
  let open = null; // the trade in progress
  let stopPrice = NaN;
  let targetPrice = NaN;
  let slDist = NaN;
  let best = NaN; // best price since entry (for the trailing stop)
  let blockSide = 0;

  const closeTrade = (i, price, reason) => {
    if (!open) return;
    trades.push({
      side: open.side,
      entryIdx: open.idx,
      entryTime: candles[open.idx].time,
      entryPrice: open.price,
      exitIdx: i,
      exitTime: candles[i].time,
      exitPrice: price,
      ret: eq / open.eqBefore - 1,
      pnl: eq - open.eqBefore,
      bars: i - open.idx,
      reason,
      risk: open.risk, // stop distance in price units (NaN when no stop loss)
      rMultiple: Number.isFinite(open.risk) && open.risk > 0 ? (open.side * (price - open.price)) / open.risk : NaN,
    });
    open = null;
  };
  const flat = () => {
    pos = 0;
    stopPrice = NaN;
    targetPrice = NaN;
    slDist = NaN;
    best = NaN;
  };

  for (let i = 0; i < n; i += 1) {
    const c = candles[i];
    times[i] = c.time;
    const prevEq = i === 0 ? s.capital : equity[i - 1];
    let enteredThisBar = false;
    let entryPriceThisBar = c.open;

    // Carry is charged by elapsed time, so weekends and missing bars use the actual gap.
    if (pos !== 0 && i > 0) {
      const elapsedDays = Math.max(0, (c.time - candles[i - 1].time) / 86400);
      const dailyBps = pos === 1 ? Number(s.longFinancingBpsPerDay) : Number(s.shortFinancingBpsPerDay);
      const rate = Number.isFinite(dailyBps) ? (dailyBps / 1e4) * elapsedDays * alloc : 0;
      if (rate) eq *= 1 - rate;
    }

    // 1) carry the position from the previous close to this open (gap)
    if (pos !== 0 && i > 0) eq *= 1 + pos * alloc * (c.open / candles[i - 1].close - 1);

    // 2) gap through the stop or the target at the open
    if (pos !== 0) {
      const gapStop = Number.isFinite(stopPrice) && ((pos === 1 && c.open <= stopPrice) || (pos === -1 && c.open >= stopPrice));
      const gapTp = Number.isFinite(targetPrice) && ((pos === 1 && c.open >= targetPrice) || (pos === -1 && c.open <= targetPrice));
      if (gapStop || gapTp) {
        eq *= 1 - sideCost * alloc;
        closeTrade(i, c.open, gapStop ? "stop" : "target");
        blockSide = pos;
        flat();
      }
    }

    // 3) the signal from the previous close becomes the target position at this open
    let target = i > 0 ? desired[i - 1] || 0 : 0;
    if (!s.allowShort && target < 0) target = 0;
    if (blockSide !== 0) {
      if (target === blockSide) target = 0;
      else blockSide = 0;
    }
    if (target !== pos) {
      if (pos !== 0) {
        eq *= 1 - sideCost * alloc;
        closeTrade(i, c.open, "signal");
        flat();
      }
      if (target !== 0) {
        const fillPrice = entryFillPrice(target, candles[i - 1]?.close, c, s);
        if (Number.isFinite(fillPrice)) {
          const eqBeforeEntry = eq;
          eq *= 1 - sideCost * alloc;
          pos = target;
          enteredThisBar = true;
          entryPriceThisBar = fillPrice;
          best = fillPrice;
          slDist = useSl ? distance(s.slType, s.slValue, fillPrice, i) : NaN;
          open = { side: target, idx: i, price: fillPrice, eqBefore: eqBeforeEntry, risk: slDist };
          const tpDist = !useTp ? NaN : s.tpType === "rr" ? (Number.isFinite(slDist) ? slDist * s.tpValue : NaN) : distance(s.tpType, s.tpValue, fillPrice, i);
          stopPrice = Number.isFinite(slDist) ? fillPrice - target * slDist : NaN;
          targetPrice = Number.isFinite(tpDist) ? fillPrice + target * tpDist : NaN;
        }
      }
    }

    // 4) intrabar: stop first, then target, otherwise ride to the close
    if (pos !== 0) {
      const hitStop = Number.isFinite(stopPrice) && ((pos === 1 && c.low <= stopPrice) || (pos === -1 && c.high >= stopPrice));
      const hitTp = Number.isFinite(targetPrice) && ((pos === 1 && c.high >= targetPrice) || (pos === -1 && c.low <= targetPrice));
      if (hitStop || hitTp) {
        const px = hitStop ? stopPrice : targetPrice;
        eq *= 1 + pos * alloc * (px / (enteredThisBar ? entryPriceThisBar : c.open) - 1);
        eq *= 1 - sideCost * alloc;
        closeTrade(i, px, hitStop ? "stop" : "target");
        blockSide = pos;
        flat();
      } else {
        eq *= 1 + pos * alloc * (c.close / (enteredThisBar ? entryPriceThisBar : c.open) - 1);
        // trailing stop moves up (long) / down (short) only AFTER the bar, so it applies from the next bar
        if (s.trailing && Number.isFinite(slDist) && !enteredThisBar) {
          best = pos === 1 ? Math.max(best, c.high) : Math.min(best, c.low);
          const trail = best - pos * slDist;
          stopPrice = pos === 1 ? Math.max(stopPrice, trail) : Math.min(stopPrice, trail);
        }
      }
    }

    position[i] = pos;
    equity[i] = eq;
    barReturns[i] = prevEq > 0 ? eq / prevEq - 1 : 0;
  }

  // close whatever is still open at the last close
  if (pos !== 0 && n > 0) {
    eq *= 1 - sideCost * alloc;
    closeTrade(n - 1, candles[n - 1].close, "end");
    equity[n - 1] = eq;
    barReturns[n - 1] = (n > 1 ? eq / equity[n - 2] : eq / s.capital) - 1;
    position[n - 1] = 0;
  }

  return { times, equity, barReturns, position, trades, settings: s };
}

/** Equal-weight portfolio of several runs on the same candles, rebalanced every bar. */
export function combinePortfolio(runs, capital) {
  if (!runs.length) return null;
  const n = runs[0].times.length;
  const equity = new Array(n);
  const barReturns = new Array(n).fill(0);
  const position = new Array(n).fill(0);
  let eq = capital;
  for (let i = 0; i < n; i += 1) {
    let r = 0;
    let inMarket = 0;
    runs.forEach((run) => {
      r += run.barReturns[i] / runs.length;
      if (run.position[i] !== 0) inMarket += 1;
    });
    eq *= 1 + r;
    equity[i] = eq;
    barReturns[i] = r;
    position[i] = inMarket / runs.length; // fraction of the portfolio invested
  }
  const trades = runs.flatMap((run) => run.trades.map((t) => ({ ...t, strategy: run.name })));
  trades.sort((a, b) => a.exitTime - b.exitTime);
  return { times: runs[0].times, equity, barReturns, position, trades };
}
